import React, { useCallback, useEffect } from "react";

import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay, Text
} from "@chakra-ui/react";

import { useDispatch, useSelector } from "react-redux";

import { Form } from "@/components/forms/Form/Form";
import { FormField } from "@/components/forms/FormField/FormField";
import { FormSelect } from "@/components/forms/FormSelect/FormSelect";
import { useCompanyMembers } from "@/hooks/features/useCompanyMembers";
import { loadCompanyMembers } from "@/store/features/companyMembers/companyMembers";
import { 
  reassignQuery, 
  selectIsReassigningQuery,
} from "@/store/features/communicationsHub/communicationsHub";

interface FormData {
  assignTo: string;
}

interface QueryReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryId: string;
  companyId: string;
  currentAssigneeId?: string;
  onReassignSuccess?: () => void;
}

export const QueryReassignModal: React.FC<QueryReassignModalProps> = ({
  isOpen,
  onClose,
  queryId,
  companyId,
  currentAssigneeId,
  onReassignSuccess
}) => {
  const { companyMembers } = useCompanyMembers();
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsReassigningQuery);

  const initialValues: FormData = {
    assignTo: currentAssigneeId || ""
  };

  useEffect(() => {
    if (isOpen && companyId) {
      dispatch(loadCompanyMembers(companyId));
    }
  }, [dispatch, companyId, isOpen]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = (data: FormData) => {
    dispatch(reassignQuery(companyId, queryId, data.assignTo, () => {
      onReassignSuccess?.();
      onClose();
    }));
  };

  const memberOptions = companyMembers.map((member) => ({
    label: `${member.first_name} ${member.last_name}`,
    secondaryLabel: member.email,
    value: member.id
  }));

  return (
    <Modal isCentered isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent p={4} borderRadius={20}>
        <ModalHeader color="secondary.600" >Reassign Query</ModalHeader>
        <ModalCloseButton />
        <Form
          onSubmit={handleSubmit}
          initialValues={initialValues}
          mode="onSubmit"
        >
          <ModalBody>
            <Text fontWeight={800} fontSize={18}>
              Please select the person you would like to assign this
              query to.
            </Text>
            <FormField
              name="assignTo"
              label="Practice names"
              control={FormSelect}
              options={memberOptions}
              searchMatchFrom="any"
              placeholder="Select a person you'd like to assign this query to"
              isSearchable
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={handleClose} mr={3}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={isLoading}
              loadingText="Reassigning..."
            >
              Reassign Query
            </Button>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};